import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import { API_ENDPOINTS } from '../../../../utils/config';
import TaxInvoiceForm from '../../sales/components/TaxInvoiceForm';
import {
  FileText,
  Search,
  AlertCircle,
  CheckCircle,
  Eye,
  Edit,
  Trash2,
  Plus,
  Check,
  Send,
  X
} from 'lucide-react';

const PurchaseInvoices = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterReverse, setFilterReverse] = useState('all');
  const [ordering, setOrdering] = useState('-invoice_date');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [selected, setSelected] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isTaxFormOpen, setIsTaxFormOpen] = useState(false);

  const [supplierList, setSupplierList] = useState([]);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [createForm, setCreateForm] = useState({
    supplier: '',
    invoice_number: '',
    debit_note_number: '',
    invoice_date: '',
    due_date: '',
    status: 'draft',
    type: 'purchase_invoice',
    notes: '',
    terms: '',
    reference_invoice: '',
    specialise_supply: false,
    apply_reverse_charge: false,
    claim_itc: true,
    items: [
      {
        id: 1,
        description: '',
        quantity: 1,
        rate: 0,
        amount: 0,
        tax_rate: 18,
        tax_amount: 0
      }
    ],
    subtotal: 0,
    tax_amount: 0,
    total_amount: 0
  });
  const [editForm, setEditForm] = useState({
    supplier: '',
    invoice_number: '',
    debit_note_number: '',
    invoice_date: '',
    due_date: '',
    status: 'draft',
    type: 'purchase_invoice',
    notes: '',
    terms: '',
    reference_invoice: '',
    specialise_supply: false,
    apply_reverse_charge: false,
    claim_itc: true,
    items: [
      {
        id: 1,
        description: '',
        quantity: 1,
        rate: 0,
        amount: 0,
        tax_rate: 18,
        tax_amount: 0
      }
    ],
    subtotal: 0,
    tax_amount: 0,
    total_amount: 0
  });

  const getErrorMessage = (err, fallback) => {
    const respData = err?.response?.data;
    const message =
      respData?.message ||
      respData?.detail ||
      respData?.error ||
      (typeof respData === 'string' ? respData : null) ||
      err?.message ||
      fallback;
    if (typeof message === 'string') return message;
    try {
      return JSON.stringify(message || fallback);
    } catch {
      return fallback;
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      };
    }
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    };
  };

  const formatCurrency = (value) =>
    `₹${(parseFloat(value || 0) || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

  const toMoney2 = (value, defaultValue = '0.00') => {
    const num = Number(value);
    if (Number.isNaN(num)) return defaultValue;
    return num.toFixed(2);
  };

  const exceedsMaxDigits = (moneyString) => {
    if (!moneyString) return false;
    const digitsOnly = moneyString.replace('.', '');
    return digitsOnly.length > 15;
  };

  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const taxAmount = items.reduce((sum, item) => sum + (parseFloat(item.tax_amount) || 0), 0);
    const totalAmount = subtotal + taxAmount;
    return {
      subtotal: toMoney2(subtotal),
      tax_amount: toMoney2(taxAmount),
      total_amount: toMoney2(totalAmount)
    };
  };

  const updateItem = (formSetter, index, field, value) => {
    formSetter(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      if (field === 'quantity' || field === 'rate') {
        const quantity = parseFloat(newItems[index].quantity) || 1;
        const rate = parseFloat(newItems[index].rate) || 0;
        newItems[index].amount = toMoney2(quantity * rate);
        const taxRate = parseFloat(newItems[index].tax_rate) || 0;
        newItems[index].tax_amount = toMoney2((quantity * rate * taxRate) / 100);
      } else if (field === 'tax_rate') {
        const quantity = parseFloat(newItems[index].quantity) || 1;
        const rate = parseFloat(newItems[index].rate) || 0;
        const taxRate = parseFloat(value) || 0;
        newItems[index].tax_amount = toMoney2((quantity * rate * taxRate) / 100);
      }
      const totals = calculateTotals(newItems);
      return { ...prev, items: newItems, ...totals };
    });
  };

  const addItem = (formSetter) => {
    formSetter(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: prev.items.length + 1,
          description: '',
          quantity: 1,
          rate: 0,
          amount: 0,
          tax_rate: 18,
          tax_amount: 0
        }
      ]
    }));
  };

  const removeItem = (formSetter, index) => {
    formSetter(prev => {
      const newItems = prev.items.filter((_, i) => i !== index);
      const totals = calculateTotals(newItems);
      return { ...prev, items: newItems, ...totals };
    });
  };

  // Helper function to get supplier display name
  const getSupplierDisplayName = (supplier) => {
    if (!supplier) return '';
    return supplier.supplier_data?.supplier_name ||
      supplier.supplier_name ||
      supplier.name ||
      supplier.company_name ||
      `Supplier ${supplier.id}`;
  };

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.SUPPLIERS_LIST, { headers: getAuthHeaders() });
      const data = res.data;
      const normalized = Array.isArray(data) ? data : (data.results || data.data || []);
      setSupplierList(normalized);
      return normalized; // Return the suppliers data
    } catch (err) {
      // ignore
      return [];
    }
  };

  const fetchCompanyProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };
      const response = await axios.get(API_ENDPOINTS.COMPANY_PROFILE_LIST, { headers });
      const profiles = Array.isArray(response.data) ? response.data : (response.data.results || []);
      if (profiles.length > 0) {
        const profile = profiles[0];
        // Handle logo URL
        let logoUrl = null;
        if (profile.company_logo || profile.logo) {
          const rawLogo = profile.company_logo || profile.logo;
          if (rawLogo.startsWith('http')) {
            logoUrl = rawLogo;
          } else {
            const baseUrl = (API_ENDPOINTS.API_BASE_URL || '').replace('/api', '');
            logoUrl = rawLogo.startsWith('/') ? `${baseUrl}${rawLogo}` : `${baseUrl}/${rawLogo}`;
          }
        }
        setCompanyProfile({ ...profile, logoUrl });
      }
    } catch (err) {
      // ignore
    }
  };

  const fetchItems = async (page = 1) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication required. Please log in again.');
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('type', 'purchase_invoice');
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterReverse !== 'all') params.append('apply_reverse_charge', filterReverse);
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', page);
      params.append('ordering', ordering || '-invoice_date');

      const response = await axios.get(`${API_ENDPOINTS.PURCHASE_INVOICES_LIST}?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      const data = response.data;
      const normalized = Array.isArray(data) ? data : data.results || [];
      const normalizedCount = Array.isArray(data) ? data.length : data.count || normalized.length || 0;

      setItems(normalized);
      setTotalCount(normalizedCount);
      setNextPage(Array.isArray(data) ? null : data.next);
      setPrevPage(Array.isArray(data) ? null : data.previous);
      setCurrentPage(page);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch purchase invoices'));
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(1);
    fetchSuppliers();
    fetchCompanyProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterReverse, ordering]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchItems(1), 400);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const openTaxForm = () => setIsTaxFormOpen(true);

  const openCreate = () => {
    const today = new Date().toISOString().split('T')[0];
    setCreateForm({
      supplier: '',
      invoice_number: '',
      debit_note_number: '',
      invoice_date: today,
      due_date: today,
      status: 'draft',
      type: 'purchase_invoice',
      notes: '',
      terms: '',
      reference_invoice: '',
      specialise_supply: false,
      apply_reverse_charge: false,
      claim_itc: true,
      items: [
        {
          id: 1,
          description: '',
          quantity: 1,
          rate: 0,
          amount: 0,
          tax_rate: 18,
          tax_amount: 0
        }
      ],
      subtotal: '0.00',
      tax_amount: '0.00',
      total_amount: '0.00'
    });
    setIsCreateOpen(true);
    setError('');
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication required. Please log in again.');
      return;
    }
    if (!createForm.supplier) {
      setError('Please select a supplier');
      return;
    }
    const items = createForm.items || [];
    if (!items.length) {
      setError('Please add at least one item with a description');
      return;
    }
    const subtotalMoney = createForm.subtotal;
    const taxMoney = createForm.tax_amount;
    const totalMoney = createForm.total_amount;
    if (exceedsMaxDigits(subtotalMoney) || exceedsMaxDigits(taxMoney) || exceedsMaxDigits(totalMoney)) {
      setError('Amounts must not exceed 15 total digits.');
      return;
    }
    setIsCreateLoading(true);
    try {
      const payload = {
        supplier: parseInt(createForm.supplier),
        invoice_number: createForm.invoice_number || null,
        debit_note_number: createForm.debit_note_number || null,
        invoice_date: createForm.invoice_date || null,
        due_date: createForm.due_date || createForm.invoice_date || null,
        subtotal: subtotalMoney,
        tax_amount: taxMoney,
        total_amount: totalMoney,
        status: createForm.status,
        type: createForm.type,
        notes: createForm.notes,
        terms: createForm.terms,
        reference_invoice: createForm.reference_invoice ? (parseInt(createForm.reference_invoice) || createForm.reference_invoice) : null,
        specialise_supply: !!createForm.specialise_supply,
        apply_reverse_charge: !!createForm.apply_reverse_charge,
        claim_itc: !!createForm.claim_itc,
        items: items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity).toFixed(2),
          rate: parseFloat(item.rate).toFixed(2),
          amount: parseFloat(item.amount).toFixed(2),
          tax_rate: parseFloat(item.tax_rate).toFixed(2),
          tax_amount: parseFloat(item.tax_amount).toFixed(2)
        }))
      };
      const response = await axios.post(API_ENDPOINTS.PURCHASE_INVOICES_LIST, payload, { headers: getAuthHeaders() });
      const msg = response?.data?.message || 'Purchase invoice created successfully';
      setSuccessMessage(msg);
      setIsCreateOpen(false);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchItems(currentPage);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create purchase invoice'));
    } finally {
      setIsCreateLoading(false);
    }
  };

  const handleView = async (row) => {
    try {
      setIsLoading(true);
      // Fetch suppliers to get supplier names
      const suppliers = await fetchSuppliers();
      const response = await axios.get(API_ENDPOINTS.PURCHASE_INVOICES_DETAIL(row.id), { headers: getAuthHeaders() });
      const data = response.data;

      // If supplier is an object, extract the name; if it's an ID, find it in suppliers list
      let supplierName = data.supplier_name || 'N/A';
      if (data.supplier) {
        if (typeof data.supplier === 'object' && data.supplier.id) {
          const supplier = suppliers.find(s => s.id === data.supplier.id);
          supplierName = supplier ? getSupplierDisplayName(supplier) : (data.supplier.supplier_data?.supplier_name || data.supplier.supplier_name || 'N/A');
        } else if (typeof data.supplier === 'number' || typeof data.supplier === 'string') {
          const supplier = suppliers.find(s => s.id === parseInt(data.supplier));
          supplierName = supplier ? getSupplierDisplayName(supplier) : 'N/A';
        }
      }

      // Resolve reference_invoice for display - API may return object or ID
      const refInvoiceDisplay = data.reference_invoice
        ? (typeof data.reference_invoice === 'object' && data.reference_invoice?.invoice_number
          ? data.reference_invoice.invoice_number
          : (data.reference_invoice?.id ?? data.reference_invoice))
        : null;

      const mergedItems = data.items_detail?.length ? data.items_detail : (data.items || []);
      setSelected({ ...data, items_detail: mergedItems, supplier_name: supplierName, reference_invoice_display: refInvoiceDisplay });
      setIsDetailsOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch purchase invoice details'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEdit = async (row) => {
    try {
      setIsEditLoading(true);
      // Ensure suppliers are loaded first and get the data directly
      const suppliers = await fetchSuppliers();
      const response = await axios.get(API_ENDPOINTS.PURCHASE_INVOICES_DETAIL(row.id), { headers: getAuthHeaders() });
      const data = response.data || row;
      setSelected(data);

      // Extract supplier ID - handle both object and ID cases
      let supplierId = '';
      if (data.supplier) {
        if (typeof data.supplier === 'object' && data.supplier.id) {
          supplierId = data.supplier.id;
        } else if (typeof data.supplier === 'number' || typeof data.supplier === 'string') {
          supplierId = data.supplier;
        }
      }

      // Resolve reference_invoice - API may return object {id, invoice_number} or ID
      let referenceInvoiceVal = '';
      if (data.reference_invoice) {
        if (typeof data.reference_invoice === 'object' && data.reference_invoice.id != null) {
          referenceInvoiceVal = String(data.reference_invoice.id);
        } else if (typeof data.reference_invoice === 'number' || typeof data.reference_invoice === 'string') {
          referenceInvoiceVal = String(data.reference_invoice);
        }
      }

      const rawItems = data.items_detail?.length ? data.items_detail : (data.items?.length ? data.items : []);
      const items = rawItems.length > 0 ? rawItems.map((item, idx) => ({
        id: idx + 1,
        description: item.description || '',
        quantity: item.quantity || 1,
        rate: item.rate || 0,
        amount: item.amount || 0,
        tax_rate: item.tax_rate || 18,
        tax_amount: item.tax_amount || 0
      })) : [
        {
          id: 1,
          description: '',
          quantity: 1,
          rate: 0,
          amount: 0,
          tax_rate: 18,
          tax_amount: 0
        }
      ];

      const totals = calculateTotals(items);

      setEditForm({
        supplier: supplierId ? String(supplierId) : '',
        invoice_number: data.invoice_number || '',
        debit_note_number: data.debit_note_number || '',
        invoice_date: data.invoice_date ? data.invoice_date.split('T')[0] : '',
        due_date: data.due_date ? data.due_date.split('T')[0] : (data.invoice_date ? data.invoice_date.split('T')[0] : ''),
        status: data.status || 'draft',
        type: data.type || 'purchase_invoice',
        notes: data.notes || '',
        terms: data.terms || '',
        reference_invoice: referenceInvoiceVal,
        specialise_supply: !!data.specialise_supply,
        apply_reverse_charge: !!data.apply_reverse_charge,
        claim_itc: data.claim_itc !== undefined ? !!data.claim_itc : true,
        items,
        ...totals
      });
      setIsEditOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load purchase invoice for editing'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication required. Please log in again.');
      return;
    }
    if (!selected?.id) return;
    if (!editForm.supplier) {
      setError('Please select a supplier');
      return;
    }
    const items = editForm.items || [];
    if (!items.length) {
      setError('Please add at least one invoice item');
      return;
    }
    const subtotalMoney = editForm.subtotal;
    const taxMoney = editForm.tax_amount;
    const totalMoney = editForm.total_amount;
    if (exceedsMaxDigits(subtotalMoney) || exceedsMaxDigits(taxMoney) || exceedsMaxDigits(totalMoney)) {
      setError('Amounts must not exceed 15 total digits.');
      return;
    }
    setIsEditLoading(true);
    try {
      const payload = {
        supplier: parseInt(editForm.supplier),
        invoice_number: editForm.invoice_number || null,
        debit_note_number: editForm.debit_note_number || null,
        invoice_date: editForm.invoice_date || null,
        due_date: editForm.due_date || editForm.invoice_date || null,
        subtotal: subtotalMoney,
        tax_amount: taxMoney,
        total_amount: totalMoney,
        status: editForm.status,
        type: editForm.type,
        notes: editForm.notes,
        terms: editForm.terms,
        reference_invoice: editForm.reference_invoice ? (parseInt(editForm.reference_invoice) || editForm.reference_invoice) : null,
        specialise_supply: !!editForm.specialise_supply,
        apply_reverse_charge: !!editForm.apply_reverse_charge,
        claim_itc: !!editForm.claim_itc,
        items: items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity).toFixed(2),
          rate: parseFloat(item.rate).toFixed(2),
          amount: parseFloat(item.amount).toFixed(2),
          tax_rate: parseFloat(item.tax_rate).toFixed(2),
          tax_amount: parseFloat(item.tax_amount).toFixed(2)
        }))
      };
      const response = await axios.put(
        API_ENDPOINTS.PURCHASE_INVOICES_UPDATE(selected.id),
        payload,
        { headers: getAuthHeaders() }
      );
      const msg = response?.data?.message || 'Purchase invoice updated successfully';
      setSuccessMessage(msg);
      setIsEditOpen(false);
      setSelected(null);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchItems(currentPage);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update purchase invoice'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleOpenDelete = (row) => {
    setDeleteTarget(row);
    setIsDeleteOpen(true);
    setError('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleteLoading(true);
    try {
      const response = await axios.delete(API_ENDPOINTS.PURCHASE_INVOICES_DELETE(deleteTarget.id), {
        headers: getAuthHeaders()
      });
      const msg = response?.data?.message || 'Purchase invoice deleted successfully';
      setSuccessMessage(msg);
      setIsDeleteOpen(false);
      setDeleteTarget(null);
      fetchItems(currentPage);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete purchase invoice'));
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const doAction = async (id, endpoint, successFallback) => {
    setIsActionLoading(true);
    try {
      const response = await axios.post(endpoint(id), {}, { headers: getAuthHeaders() });
      const msg = response?.data?.message || response?.data?.status || successFallback;
      setSuccessMessage(msg);
      fetchItems(currentPage);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Action failed'));
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleMarkPaid = (row) => doAction(row.id, API_ENDPOINTS.PURCHASE_INVOICES_MARK_PAID, 'Invoice marked as paid');
  const handleMarkSent = (row) => doAction(row.id, API_ENDPOINTS.PURCHASE_INVOICES_MARK_SENT, 'Invoice marked as sent');

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span>{successMessage}</span>
          </div>
          <button onClick={() => setSuccessMessage('')} className="text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-muted-foreground">Total Purchase Invoices</p>
          <p className="text-2xl font-bold text-foreground">{totalCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search purchase invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            value={filterReverse}
            onChange={(e) => setFilterReverse(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">Reverse Charge?</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
          <select
            value={ordering}
            onChange={(e) => setOrdering(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="-invoice_date">Newest</option>
            <option value="invoice_date">Oldest</option>
            <option value="-total_amount">Amount: High to Low</option>
            <option value="total_amount">Amount: Low to High</option>
          </select>
          <Button onClick={openTaxForm}>
            <Plus className="w-4 h-4 mr-2" />
            New Purchase Invoice
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading purchase invoices...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-muted-foreground">No purchase invoices found</p>
            </div>
          ) : (
            <>
              <table className="w-full min-w-[1100px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">{row.invoice_number || row.debit_note_number || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{row.supplier_name || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        {row.invoice_date ? new Date(row.invoice_date).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-foreground">
                        {formatCurrency(row.total_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${row.status === 'paid'
                            ? 'bg-green-100 text-green-800'
                            : row.status === 'overdue'
                              ? 'bg-red-100 text-red-800'
                              : row.status === 'sent'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                        >
                          {row.status || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground capitalize">
                        {row.type?.replace('_', ' ') || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Button variant="ghost" size="sm" title="View" onClick={() => handleView(row)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" title="Edit" onClick={() => handleOpenEdit(row)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" title="Delete" onClick={() => handleOpenDelete(row)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" title="Mark Sent" onClick={() => handleMarkSent(row)} disabled={isActionLoading}>
                            <Send className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" title="Mark Paid" onClick={() => handleMarkPaid(row)} disabled={isActionLoading}>
                            <Check className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {(nextPage || prevPage) && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">Showing page {currentPage} of {Math.ceil(totalCount / 10)}</div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={() => fetchItems(currentPage - 1)} disabled={!prevPage}>
                      Previous
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => fetchItems(currentPage + 1)} disabled={!nextPage}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {isDetailsOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Purchase Invoice Details</h3>
              <Button variant="outline" onClick={() => { setIsDetailsOpen(false); setSelected(null); }}>Close</Button>
            </div>
            <div className="p-6 space-y-6">
              {/* Company Header */}
              <div className="border-b-2 border-gray-300 pb-4">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  <div className="flex-1 w-full">
                    <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
                      {companyProfile?.company_name || companyProfile?.name || 'COMPANY NAME'}
                    </h1>
                    <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                      <p>
                        Address: {companyProfile?.address || 'Company Address Line 1, City, State, PIN'}
                      </p>
                      <p className="break-words">
                        Mobile: {companyProfile?.phone || '+91 1234567890'} | Email: {companyProfile?.email || 'info@company.com'}
                      </p>
                      <p className="break-words">
                        GSTIN: {companyProfile?.gstin || '29XXXXX1234X1XX'}
                      </p>
                    </div>
                  </div>
                  <div className="text-left sm:text-right w-full sm:w-auto">
                    {companyProfile?.logoUrl ? (
                      <img
                        src={companyProfile.logoUrl}
                        alt={companyProfile.company_name || 'Company Logo'}
                        className="h-12 sm:h-16 object-contain ml-auto"
                      />
                    ) : (
                      <div className="text-base sm:text-lg font-semibold text-primary">LOGO</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Document Type Banner */}
              <div className="bg-foreground text-white text-center py-2 sm:py-3 font-bold text-base sm:text-lg">
                PURCHASE INVOICE
              </div>

              {/* Invoice Details */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice No</label>
                  <p className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm">{selected.invoice_number || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                  <p className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm">{selected.invoice_date ? new Date(selected.invoice_date).toLocaleDateString() : 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <p className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm">{selected.due_date ? new Date(selected.due_date).toLocaleDateString() : 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <p className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm capitalize">{selected.status || 'N/A'}</p>
                </div>
              </div>

              {/* Supplier Details */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-md font-semibold text-foreground mb-4">Supplier Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                    <p className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm">{selected.supplier_name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <p className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm capitalize">{selected.type?.replace('_', ' ') || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Services Table */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-md font-semibold text-foreground mb-4">Services</h4>
                <div className="overflow-x-auto rounded border border-gray-200">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Description</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Quantity</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Rate</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Amount</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Tax Rate</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Tax Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.items_detail && selected.items_detail.length > 0 ? (
                        selected.items_detail.map((item, idx) => (
                          <tr key={item.id || idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2 border-b text-sm">{item.description || 'N/A'}</td>
                            <td className="px-4 py-2 border-b text-sm">{item.quantity || 'N/A'}</td>
                            <td className="px-4 py-2 border-b text-sm">{formatCurrency(item.rate)}</td>
                            <td className="px-4 py-2 border-b text-sm">{formatCurrency(item.amount)}</td>
                            <td className="px-4 py-2 border-b text-sm">{item.tax_rate ? `${item.tax_rate}%` : 'N/A'}</td>
                            <td className="px-4 py-2 border-b text-sm">{formatCurrency(item.tax_amount)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="px-4 py-4 text-center text-sm text-gray-500">No items found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(selected.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Tax Amount:</span>
                      <span>{formatCurrency(selected.tax_amount)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-base border-t pt-2">
                      <span>Total:</span>
                      <span>{formatCurrency(selected.total_amount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={!!selected.specialise_supply}
                    disabled
                    className="h-4 w-4 text-primary border-gray-300 rounded"
                  />
                  <label className="text-sm text-gray-700">Specialised Supply</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={!!selected.apply_reverse_charge}
                    disabled
                    className="h-4 w-4 text-primary border-gray-300 rounded"
                  />
                  <label className="text-sm text-gray-700">Apply Reverse Charge</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={!!selected.claim_itc}
                    disabled
                    className="h-4 w-4 text-primary border-gray-300 rounded"
                  />
                  <label className="text-sm text-gray-700">Claim ITC</label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    rows={3}
                    value={selected.notes || ''}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Terms</label>
                  <textarea
                    rows={3}
                    value={selected.terms || ''}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                  />
                </div>
              </div>

              {/* Signature */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-end">
                  <div className="text-center">
                    <div className="border-b border-gray-400 w-48 mb-2"></div>
                    <p className="text-sm text-gray-600">Authorized Signature</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => { setIsDetailsOpen(false); setSelected(null); }}>Close</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isTaxFormOpen && (
        <TaxInvoiceForm
          isOpen={isTaxFormOpen}
          onClose={() => setIsTaxFormOpen(false)}
          onSave={() => { setIsTaxFormOpen(false); fetchItems(currentPage); }}
          invoiceType="purchase-invoice"
        />
      )}

      {/* Create Modal */}
      {isCreateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Create Purchase Invoice</h3>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Close</Button>
            </div>
            <form onSubmit={handleSubmitCreate} className="p-6 space-y-6">
              {/* Company Header */}
              <div className="border-b-2 border-gray-300 pb-4">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  <div className="flex-1 w-full">
                    <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
                      {companyProfile?.company_name || companyProfile?.name || 'COMPANY NAME'}
                    </h1>
                    <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                      <p>
                        Address: {companyProfile?.address || 'Company Address Line 1, City, State, PIN'}
                      </p>
                      <p className="break-words">
                        Mobile: {companyProfile?.phone || '+91 1234567890'} | Email: {companyProfile?.email || 'info@company.com'}
                      </p>
                      <p className="break-words">
                        GSTIN: {companyProfile?.gstin || '29XXXXX1234X1XX'}
                      </p>
                    </div>
                  </div>
                  <div className="text-left sm:text-right w-full sm:w-auto">
                    {companyProfile?.logoUrl ? (
                      <img
                        src={companyProfile.logoUrl}
                        alt={companyProfile.company_name || 'Company Logo'}
                        className="h-12 sm:h-16 object-contain ml-auto"
                      />
                    ) : (
                      <div className="text-base sm:text-lg font-semibold text-primary">LOGO</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Document Type Banner */}
              <div className="bg-foreground text-white text-center py-2 sm:py-3 font-bold text-base sm:text-lg">
                PURCHASE INVOICE
              </div>

              {/* Invoice Details */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice No</label>
                  <input
                    type="text"
                    value={createForm.invoice_number}
                    onChange={(e) => setCreateForm((p) => ({ ...p, invoice_number: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Auto-generated"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={createForm.invoice_date}
                    onChange={(e) => setCreateForm((p) => ({ ...p, invoice_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={createForm.due_date}
                    onChange={(e) => setCreateForm((p) => ({ ...p, due_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={createForm.status}
                    onChange={(e) => setCreateForm((p) => ({ ...p, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
              </div>

              {/* Supplier Details */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-md font-semibold text-foreground mb-4">Supplier Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                    <select
                      value={String(createForm.supplier || '')}
                      onChange={(e) => setCreateForm((p) => ({ ...p, supplier: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">Select supplier</option>
                      {supplierList.map((s) => (
                        <option key={s.id} value={String(s.id)}>
                          {getSupplierDisplayName(s)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={createForm.type}
                      onChange={(e) => setCreateForm((p) => ({ ...p, type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="purchase_invoice">Purchase Invoice</option>
                      <option value="purchase_order">Purchase Order</option>
                      <option value="debit_note">Debit Note</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Services Table */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-md font-semibold text-foreground">Services</h4>
                  <Button type="button" variant="outline" size="sm" onClick={() => addItem(setCreateForm)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </div>
                <div className="overflow-x-auto rounded border border-gray-200">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Description</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Quantity</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Rate</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Amount</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Tax Rate</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Tax Amount</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {createForm.items.map((item, index) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 border-b">
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => updateItem(setCreateForm, index, 'description', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              placeholder="Item description"
                            />
                          </td>
                          <td className="px-4 py-2 border-b">
                            <input
                              type="number"
                              step="1"
                              value={item.quantity}
                              onChange={(e) => updateItem(setCreateForm, index, 'quantity', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </td>
                          <td className="px-4 py-2 border-b">
                            <input
                              type="number"
                              step="0.01"
                              value={item.rate}
                              onChange={(e) => updateItem(setCreateForm, index, 'rate', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </td>
                          <td className="px-4 py-2 border-b text-sm">{formatCurrency(item.amount)}</td>
                          <td className="px-4 py-2 border-b">
                            <input
                              type="number"
                              step="0.01"
                              value={item.tax_rate}
                              onChange={(e) => updateItem(setCreateForm, index, 'tax_rate', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </td>
                          <td className="px-4 py-2 border-b text-sm">{formatCurrency(item.tax_amount)}</td>
                          <td className="px-4 py-2 border-b">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(setCreateForm, index)}
                              disabled={createForm.items.length === 1}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(createForm.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Tax Amount:</span>
                      <span>{formatCurrency(createForm.tax_amount)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-base border-t pt-2">
                      <span>Total:</span>
                      <span>{formatCurrency(createForm.total_amount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    id="specialise_supply_create"
                    type="checkbox"
                    checked={createForm.specialise_supply}
                    onChange={(e) => setCreateForm((p) => ({ ...p, specialise_supply: e.target.checked }))}
                    className="h-4 w-4 text-primary border-gray-300 rounded"
                  />
                  <label htmlFor="specialise_supply_create" className="text-sm text-gray-700">Specialised Supply</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    id="reverse_charge_create"
                    type="checkbox"
                    checked={createForm.apply_reverse_charge}
                    onChange={(e) => setCreateForm((p) => ({ ...p, apply_reverse_charge: e.target.checked }))}
                    className="h-4 w-4 text-primary border-gray-300 rounded"
                  />
                  <label htmlFor="reverse_charge_create" className="text-sm text-gray-700">Apply Reverse Charge</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    id="claim_itc_create"
                    type="checkbox"
                    checked={createForm.claim_itc}
                    onChange={(e) => setCreateForm((p) => ({ ...p, claim_itc: e.target.checked }))}
                    className="h-4 w-4 text-primary border-gray-300 rounded"
                  />
                  <label htmlFor="claim_itc_create" className="text-sm text-gray-700">Claim ITC</label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    rows={3}
                    value={createForm.notes}
                    onChange={(e) => setCreateForm((p) => ({ ...p, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Add notes"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Terms</label>
                  <textarea
                    rows={3}
                    value={createForm.terms}
                    onChange={(e) => setCreateForm((p) => ({ ...p, terms: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Add terms"
                  />
                </div>
              </div>

              {/* Signature */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-end">
                  <div className="text-center">
                    <div className="border-b border-gray-400 w-48 mb-2"></div>
                    <p className="text-sm text-gray-600">Authorized Signature</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={isCreateLoading}>{isCreateLoading ? 'Creating...' : 'Create'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Update Purchase Invoice</h3>
              <Button variant="outline" onClick={() => { setIsEditOpen(false); setSelected(null); }}>Close</Button>
            </div>
            <form onSubmit={handleSubmitEdit} className="p-6 space-y-6">
              {/* Company Header */}
              <div className="border-b-2 border-gray-300 pb-4">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  <div className="flex-1 w-full">
                    <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
                      {companyProfile?.company_name || companyProfile?.name || 'COMPANY NAME'}
                    </h1>
                    <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                      <p>
                        Address: {companyProfile?.address || 'Company Address Line 1, City, State, PIN'}
                      </p>
                      <p className="break-words">
                        Mobile: {companyProfile?.phone || '+91 1234567890'} | Email: {companyProfile?.email || 'info@company.com'}
                      </p>
                      <p className="break-words">
                        GSTIN: {companyProfile?.gstin || '29XXXXX1234X1XX'}
                      </p>
                    </div>
                  </div>
                  <div className="text-left sm:text-right w-full sm:w-auto">
                    {companyProfile?.logoUrl ? (
                      <img
                        src={companyProfile.logoUrl}
                        alt={companyProfile.company_name || 'Company Logo'}
                        className="h-12 sm:h-16 object-contain ml-auto"
                      />
                    ) : (
                      <div className="text-base sm:text-lg font-semibold text-primary">LOGO</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Document Type Banner */}
              <div className="bg-foreground text-white text-center py-2 sm:py-3 font-bold text-base sm:text-lg">
                PURCHASE INVOICE
              </div>

              {/* Invoice Details */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice No</label>
                  <input
                    type="text"
                    value={editForm.invoice_number}
                    onChange={(e) => setEditForm((p) => ({ ...p, invoice_number: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={editForm.invoice_date}
                    onChange={(e) => setEditForm((p) => ({ ...p, invoice_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={editForm.due_date}
                    onChange={(e) => setEditForm((p) => ({ ...p, due_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
              </div>

              {/* Supplier Details */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-md font-semibold text-foreground mb-4">Supplier Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                    <select
                      value={String(editForm.supplier || '')}
                      onChange={(e) => setEditForm((p) => ({ ...p, supplier: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">Select supplier</option>
                      {supplierList.map((s) => (
                        <option key={s.id} value={String(s.id)}>
                          {getSupplierDisplayName(s)}
                        </option>
                      ))}
                      {selected && editForm.supplier &&
                        !supplierList.find(s => String(s.id) === String(editForm.supplier)) &&
                        selected.supplier_name && (
                          <option value={String(editForm.supplier)}>
                            {selected.supplier_name}
                          </option>
                        )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={editForm.type}
                      onChange={(e) => setEditForm((p) => ({ ...p, type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="purchase_invoice">Purchase Invoice</option>
                      <option value="purchase_order">Purchase Order</option>
                      <option value="debit_note">Debit Note</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Services Table */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-md font-semibold text-foreground">Services</h4>
                  <Button type="button" variant="outline" size="sm" onClick={() => addItem(setEditForm)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </div>
                <div className="overflow-x-auto rounded border border-gray-200">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Description</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Quantity</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Rate</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Amount</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Tax Rate</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Tax Amount</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editForm.items.map((item, index) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 border-b">
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => updateItem(setEditForm, index, 'description', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              placeholder="Item description"
                            />
                          </td>
                          <td className="px-4 py-2 border-b">
                            <input
                              type="number"
                              step="1"
                              value={item.quantity}
                              onChange={(e) => updateItem(setEditForm, index, 'quantity', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </td>
                          <td className="px-4 py-2 border-b">
                            <input
                              type="number"
                              step="0.01"
                              value={item.rate}
                              onChange={(e) => updateItem(setEditForm, index, 'rate', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </td>
                          <td className="px-4 py-2 border-b text-sm">{formatCurrency(item.amount)}</td>
                          <td className="px-4 py-2 border-b">
                            <input
                              type="number"
                              step="0.01"
                              value={item.tax_rate}
                              onChange={(e) => updateItem(setEditForm, index, 'tax_rate', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </td>
                          <td className="px-4 py-2 border-b text-sm">{formatCurrency(item.tax_amount)}</td>
                          <td className="px-4 py-2 border-b">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(setEditForm, index)}
                              disabled={editForm.items.length === 1}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(editForm.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Tax Amount:</span>
                      <span>{formatCurrency(editForm.tax_amount)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-base border-t pt-2">
                      <span>Total:</span>
                      <span>{formatCurrency(editForm.total_amount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    id="specialise_supply_edit"
                    type="checkbox"
                    checked={editForm.specialise_supply}
                    onChange={(e) => setEditForm((p) => ({ ...p, specialise_supply: e.target.checked }))}
                    className="h-4 w-4 text-primary border-gray-300 rounded"
                  />
                  <label htmlFor="specialise_supply_edit" className="text-sm text-gray-700">Specialised Supply</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    id="reverse_charge_edit"
                    type="checkbox"
                    checked={editForm.apply_reverse_charge}
                    onChange={(e) => setEditForm((p) => ({ ...p, apply_reverse_charge: e.target.checked }))}
                    className="h-4 w-4 text-primary border-gray-300 rounded"
                  />
                  <label htmlFor="reverse_charge_edit" className="text-sm text-gray-700">Apply Reverse Charge</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    id="claim_itc_edit"
                    type="checkbox"
                    checked={editForm.claim_itc}
                    onChange={(e) => setEditForm((p) => ({ ...p, claim_itc: e.target.checked }))}
                    className="h-4 w-4 text-primary border-gray-300 rounded"
                  />
                  <label htmlFor="claim_itc_edit" className="text-sm text-gray-700">Claim ITC</label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    rows={3}
                    value={editForm.notes}
                    onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Add notes"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Terms</label>
                  <textarea
                    rows={3}
                    value={editForm.terms}
                    onChange={(e) => setEditForm((p) => ({ ...p, terms: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Add terms"
                  />
                </div>
              </div>

              {/* Signature */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-end">
                  <div className="text-center">
                    <div className="border-b border-gray-400 w-48 mb-2"></div>
                    <p className="text-sm text-gray-600">Authorized Signature</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => { setIsEditOpen(false); setSelected(null); }}>Cancel</Button>
                <Button type="submit" disabled={isEditLoading}>{isEditLoading ? 'Updating...' : 'Update'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {isDeleteOpen && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-foreground">Delete Purchase Invoice</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Are you sure you want to delete{' '}
                <span className="font-semibold">{deleteTarget.invoice_number || deleteTarget.debit_note_number || `#${deleteTarget.id}`}</span>?
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setIsDeleteOpen(false); setDeleteTarget(null); }} disabled={isDeleteLoading}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleteLoading}>
                {isDeleteLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseInvoices;

